<template>
  <div class="settings-page pa-4">
    <v-card outlined>
      <v-card-title class="settings-title">
        <div>
          <div class="headline">Application settings</div>
          <div class="subtitle-2 grey--text text--darken-1 mt-1">
            Change runtime routing without editing environment files or
            rebuilding the app.
          </div>
        </div>
        <v-spacer />
        <v-btn
          icon
          :loading="loading"
          title="Reload settings"
          @click="loadSettings"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>

      <v-alert type="info" text class="mx-4 mt-2 mb-0">
        Overrides activate immediately for future operations. Existing
        autonomous sessions keep their assigned agent, and starter-credit
        changes never re-award accounts that were already granted credits.
        Clearing an override restores the environment fallback shown beside it.
      </v-alert>

      <v-card-text v-if="loading && !items.length" class="py-12 text-center">
        <v-progress-circular indeterminate color="primary" />
      </v-card-text>

      <v-card-text v-else>
        <section
          v-for="category in categories"
          :key="category"
          class="settings-section mb-6"
        >
          <div class="overline primary--text mb-2">{{ category }}</div>
          <v-card
            v-for="item in itemsForCategory(category)"
            :key="item.key"
            outlined
            class="setting-row mb-3"
          >
            <v-card-text>
              <v-row align="center">
                <v-col cols="12" md="5">
                  <div class="subtitle-1 font-weight-medium">
                    {{ item.label }}
                  </div>
                  <div class="body-2 grey--text text--darken-1 mt-1">
                    {{ item.description }}
                  </div>
                  <div class="caption grey--text mt-2">
                    Environment: {{ item.env }} = {{ item.environment_value }}
                  </div>
                </v-col>
                <v-col cols="12" sm="7" md="4">
                  <v-text-field
                    v-model="formValues[item.key]"
                    :label="fieldLabel(item)"
                    :placeholder="String(item.environment_value)"
                    type="number"
                    :min="item.minimum !== undefined ? item.minimum : 0"
                    :max="item.maximum !== undefined ? item.maximum : undefined"
                    :step="item.step || 1"
                    outlined
                    dense
                    hide-details="auto"
                    @input="markDirty(item.key)"
                  />
                </v-col>
                <v-col cols="12" sm="5" md="3" class="setting-status">
                  <v-chip
                    small
                    :color="
                      item.source === 'database' ? 'primary' : 'grey lighten-2'
                    "
                    :dark="item.source === 'database'"
                  >
                    Effective {{ effectiveValue(item)
                    }}<template v-if="item.unit"> {{ item.unit }}</template>
                  </v-chip>
                  <div class="caption grey--text mt-2">
                    {{
                      item.source === "database"
                        ? "Admin override"
                        : "Environment fallback"
                    }}
                  </div>
                  <v-btn
                    v-if="item.source === 'database'"
                    text
                    small
                    color="error"
                    class="mt-1 px-0"
                    :disabled="saving"
                    @click="resetSetting(item)"
                  >
                    Restore fallback
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </section>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-4">
        <span class="caption grey--text">
          Revision {{ revision
          }}<template v-if="updatedAt">
            · Updated {{ formatDate(updatedAt) }}</template
          >
        </span>
        <v-spacer />
        <v-btn text :disabled="saving || !hasChanges" @click="discardChanges">
          Discard
        </v-btn>
        <v-btn
          outlined
          color="primary"
          :loading="validating"
          :disabled="saving || !hasChanges"
          @click="validateChanges"
        >
          Validate
        </v-btn>
        <v-btn
          color="primary"
          :loading="saving"
          :disabled="validating || !hasChanges"
          @click="saveSettings"
        >
          Save &amp; activate
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { apiClient } from "@shared/services/api";

export default {
  name: "AdminSettings",
  data: function () {
    return {
      loading: false,
      saving: false,
      validating: false,
      revision: 0,
      updatedAt: null,
      items: [],
      formValues: {},
      dirtyKeys: {}
    };
  },
  computed: {
    categories: function () {
      var seen = {};
      return this.items
        .map(function (item) {
          return item.category;
        })
        .filter(function (category) {
          if (seen[category]) return false;
          seen[category] = true;
          return true;
        });
    },
    hasChanges: function () {
      return Object.keys(this.dirtyKeys).some(key => this.valueChanged(key));
    }
  },
  created: function () {
    this.loadSettings();
  },
  methods: {
    errorMessage: function (error, fallback) {
      var data = error && error.response ? error.response.data : null;
      if (data && data.detail) return String(data.detail);
      return fallback;
    },
    loadSettings: async function () {
      this.loading = true;
      try {
        var response = await apiClient.get("/admin/settings");
        this.applyPayload(response.data || {});
      } catch (error) {
        this.$emit(
          "error",
          this.errorMessage(error, "Could not load application settings")
        );
      } finally {
        this.loading = false;
      }
    },
    applyPayload: function (payload) {
      this.revision = Number(payload.revision || 0);
      this.updatedAt = payload.updated_at || null;
      this.items = payload.items || [];
      var values = {};
      this.items.forEach(function (item) {
        values[item.key] =
          item.override_value === null ||
          typeof item.override_value === "undefined"
            ? ""
            : String(item.override_value);
      });
      this.formValues = values;
      this.dirtyKeys = {};
    },
    itemsForCategory: function (category) {
      return this.items.filter(function (item) {
        return item.category === category;
      });
    },
    itemByKey: function (key) {
      return this.items.find(function (item) {
        return item.key === key;
      });
    },
    normalizedValue: function (key) {
      var raw = this.formValues[key];
      if (
        raw === null ||
        typeof raw === "undefined" ||
        String(raw).trim() === ""
      ) {
        return null;
      }
      return Number(raw);
    },
    valueChanged: function (key) {
      var item = this.itemByKey(key);
      if (!item) return false;
      var original =
        item.override_value === null ||
        typeof item.override_value === "undefined"
          ? null
          : Number(item.override_value);
      return this.normalizedValue(key) !== original;
    },
    markDirty: function (key) {
      this.$set(this.dirtyKeys, key, true);
    },
    changedSettings: function () {
      var changed = {};
      Object.keys(this.dirtyKeys).forEach(key => {
        if (this.valueChanged(key)) changed[key] = this.normalizedValue(key);
      });
      return changed;
    },
    effectiveValue: function (item) {
      if (this.dirtyKeys[item.key] && this.formValues[item.key] !== "") {
        return this.formValues[item.key];
      }
      if (this.dirtyKeys[item.key] && this.formValues[item.key] === "") {
        return item.environment_value;
      }
      return item.effective_value;
    },
    fieldLabel: function (item) {
      if (item.kind === "agent_id") {
        return item.required
          ? "Agent ID override"
          : "Agent ID override (0 disables)";
      }
      return item.unit ? "Override (" + item.unit + ")" : "Override value";
    },
    discardChanges: function () {
      this.applyPayload({
        revision: this.revision,
        updated_at: this.updatedAt,
        items: this.items
      });
    },
    validateChanges: async function () {
      this.validating = true;
      try {
        var response = await apiClient.post("/admin/settings/validate", {
          settings: this.changedSettings()
        });
        var agents = (response.data && response.data.agents) || [];
        var names = agents.map(function (agent) {
          return agent.name + " (#" + agent.agent_id + ")";
        });
        this.$emit(
          "success",
          names.length ? "Validated: " + names.join(", ") : "Settings are valid"
        );
      } catch (error) {
        this.$emit(
          "error",
          this.errorMessage(error, "Settings validation failed")
        );
      } finally {
        this.validating = false;
      }
    },
    saveSettings: async function () {
      var changed = this.changedSettings();
      if (Object.prototype.hasOwnProperty.call(changed, "agent_main_id")) {
        var accepted = window.confirm(
          "Change the main agent for every new normal chat? Existing open chats are not moved."
        );
        if (!accepted) return;
      }
      if (
        Object.prototype.hasOwnProperty.call(
          changed,
          "free_starter_credit_grant"
        )
      ) {
        var grantAccepted = window.confirm(
          "Change the one-time starter grant for future verified free accounts? " +
            "Accounts that already received a grant will not be credited again."
        );
        if (!grantAccepted) return;
      }
      this.saving = true;
      try {
        var response = await apiClient.put("/admin/settings", {
          revision: this.revision,
          settings: changed
        });
        this.applyPayload(response.data || {});
        await this.$store.dispatch("fetchPublicConfig");
        this.$emit("success", "Application settings saved and activated");
      } catch (error) {
        this.$emit(
          "error",
          this.errorMessage(error, "Could not save application settings")
        );
        if (error && error.response && error.response.status === 409) {
          await this.loadSettings();
        }
      } finally {
        this.saving = false;
      }
    },
    resetSetting: async function (item) {
      var accepted = window.confirm(
        "Restore " +
          item.label +
          " to its environment fallback (" +
          item.environment_value +
          ")?"
      );
      if (!accepted) return;
      this.saving = true;
      try {
        var response = await apiClient.delete(
          "/admin/settings/" + encodeURIComponent(item.key),
          { params: { revision: this.revision } }
        );
        this.applyPayload(response.data || {});
        await this.$store.dispatch("fetchPublicConfig");
        this.$emit(
          "success",
          item.label + " restored to its environment fallback"
        );
      } catch (error) {
        this.$emit(
          "error",
          this.errorMessage(error, "Could not restore this setting")
        );
        if (error && error.response && error.response.status === 409) {
          await this.loadSettings();
        }
      } finally {
        this.saving = false;
      }
    },
    formatDate: function (value) {
      try {
        return new Date(value).toLocaleString();
      } catch (error) {
        return String(value || "");
      }
    }
  }
};
</script>

<style scoped>
.settings-page {
  max-width: 1180px;
  margin: 0 auto;
}

.settings-title {
  align-items: flex-start;
}

.settings-section:last-child {
  margin-bottom: 0 !important;
}

.setting-row {
  border-color: rgba(0, 0, 0, 0.12) !important;
}

.setting-status {
  text-align: right;
}

@media (max-width: 959px) {
  .setting-status {
    text-align: left;
  }
}
</style>
